import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { fbt } from 'fbtee'
import { track } from '../lib/analytics'
import { useAuth } from '../hooks/useAuth'
import { useGroups } from '../hooks/useGroups'
import { useLatestRef } from '../hooks/useLatestRef'
import { getLinkedGroupIds, linkOrRequestGroupEvent } from '../hooks/useGroupEvents'
import { canLinkDirectly } from '@shared/groupRoles'
import { registerRsvpHandler, type RsvpAnnouncement } from '../lib/rsvpBridge'
import { LinkGroupToEventDialog } from './LinkGroupToEventDialog'

function rsvpTitle(status: RsvpAnnouncement['status']): string {
  return status === 'going'
    ? fbt("You're going", 'Toast confirming the user marked themselves going to an event')
    : fbt("You're interested", 'Toast confirming the user marked themselves interested in an event')
}

/**
 * Confirms every RSVP with a toast, and uses it to ask the question the RSVP
 * itself can't: who are you going with. Mounted once at the root, so the eight
 * surfaces that can set an RSVP don't each have to carry the prompt.
 *
 * The CTA is only offered while none of the user's groups is on the event yet —
 * once one is, they've already answered "who with" and the toast is a bare
 * confirmation. One group is the common case, so its CTA links that group
 * outright; the picker only appears when there's something to pick between.
 */
export function LinkGroupAfterRsvp() {
  const { signedInUid } = useAuth()
  const { groups, groupIds } = useGroups(signedInUid)
  const [pickerEventId, setPickerEventId] = useState<string | null>(null)

  // The handler is registered once and outlives any particular render, so it
  // reads the group state through a ref rather than re-registering on every
  // change to it.
  const stateRef = useLatestRef({ groups, groupIds, signedInUid })

  useEffect(
    () =>
      registerRsvpHandler(({ eventId, status }: RsvpAnnouncement) => {
        const { groups: myGroups, groupIds: myGroupIds, signedInUid: uid } = stateRef.current
        const title = rsvpTitle(status)
        void getLinkedGroupIds(eventId, myGroupIds).then(linkedIds => {
          // One of their groups is already on this event: they've answered "who
          // with", and asking again is nagging.
          if (linkedIds.size > 0) {
            toast.success(title)
            return
          }

          const only = myGroups.length === 1 ? myGroups[0] : null
          track('rsvp_link_prompt_shown', { event_id: eventId })
          toast.success(title, {
            description: myGroups.length
              ? fbt(
                  'Link a group so your crew can plan around you.',
                  'Toast body inviting the user to link a group to the event they just RSVPd to',
                )
              : fbt(
                  'Create a group to compare picks with your crew.',
                  'Toast body inviting a user with no groups to make one after they RSVP to an event',
                ),
            action: {
              label: only
                ? canLinkDirectly(only, uid)
                  ? fbt(
                      'Link ' + fbt.param('group', only.name),
                      'Toast button that links the user\u2019s only group to the event they just RSVPd to',
                    )
                  : fbt(
                      'Suggest to ' + fbt.param('group', only.name),
                      'Toast button that suggests the event to the admins of the user\u2019s only group, which requires approval to link',
                    )
                : myGroups.length
                  ? fbt(
                      'Link a group',
                      'Toast button that opens the group picker after the user RSVPs to an event',
                    )
                  : fbt(
                      'Create a group',
                      'Toast button that opens the create-group flow after the user RSVPs to an event',
                    ),
              onClick: () => {
                if (!only || !uid) {
                  setPickerEventId(eventId)
                  return
                }
                // Toasts its own outcome, errors included.
                linkOrRequestGroupEvent(only, eventId, uid, 'rsvp_toast').catch(() => {})
              },
            },
          })
        })
      }),
    [stateRef],
  )

  return pickerEventId ? (
    <LinkGroupToEventDialog eventId={pickerEventId} onClose={() => setPickerEventId(null)} />
  ) : null
}
